import { createHelpArticle } from '../helpModel'

export const eventArticles = [
  createHelpArticle({
    id: 'event-discover', slug: 'discover-and-open-events', title: 'Discover and open events', category: 'Events',
    description: 'Browse the Events area, search listings, and open an event to see its details and current status.',
    keywords: ['events', 'discover', 'search', 'campus', 'nearby'],
    aliases: ['find campus events', 'what is happening on campus', 'browse events'],
    steps: [
      { title: 'Open Events', body: 'Choose Events from the app navigation.' },
      { title: 'Browse or search', body: 'Use the available event filters or search field to explore listings.' },
      { title: 'Open an event', body: 'Choose an event to review its time, place or online details, organizer, audience, and status.' },
    ],
    relatedArticles: ['event-rsvp', 'event-save', 'event-attendees'],
  }),
  createHelpArticle({
    id: 'event-rsvp', slug: 'rsvp-to-an-event', title: 'RSVP, change attendance, or leave an event', category: 'Events',
    description: 'Event Detail shows the RSVP actions and current attendance status available for that event.',
    keywords: ['event rsvp', 'going', 'interested', 'waitlist', 'leave'],
    aliases: ['attend an event', 'change event attendance', 'cancel my rsvp', 'join an event'],
    steps: [
      { title: 'Open the event', body: 'Open Events and choose the event.' },
      { title: 'Use its attendance control', body: 'Choose the available Going or Interested action on Event Detail. If the event is full or closed, the screen shows its current status.' },
      { title: 'Change your response', body: 'Use the secondary RSVP action shown below the main control to leave or remove interest when available.' },
    ],
    notes: ['Actions depend on whether the event is full, ended, cancelled, or otherwise available.'],
    relatedArticles: ['event-discover', 'event-attendees', 'event-chat'],
  }),
  createHelpArticle({
    id: 'event-save', slug: 'save-and-share-an-event', title: 'Save or share an event', category: 'Events',
    description: 'Save an event for later or share its event link from Event Detail.',
    keywords: ['save event', 'bookmark', 'share event', 'calendar'],
    aliases: ['find saved events', 'send event to friend', 'add event to calendar'],
    steps: [
      { title: 'Open Event Detail', body: 'Choose the event you want to keep or share.' },
      { title: 'Save the event', body: 'Choose the Save event control. Saved events are available through the Saved filter in Events.' },
      { title: 'Share or add to calendar', body: 'Use Share event to copy or share its link, or choose Add to calendar when shown.' },
    ],
    relatedArticles: ['event-discover', 'post-save'],
  }),
  createHelpArticle({
    id: 'event-attendees', slug: 'view-event-attendees', title: 'View event attendees', category: 'Events',
    description: 'An Attendees view can show Going and Interested lists; some host-only tabs and actions are permission-limited.',
    keywords: ['event attendees', 'going list', 'interested list', 'hosts'],
    aliases: ['who is attending', 'see guest list', 'event waitlist'],
    steps: [
      { title: 'Open the event', body: 'Choose an event from Events.' },
      { title: 'Open Attendees', body: 'Use the attendee count or attendees control when it is available.' },
      { title: 'Choose a list', body: 'Select Going or Interested. Waitlist and attendee-management controls are shown only to eligible hosts.' },
    ],
    notes: ['The lists and member details available to you depend on event access and your role.'],
    relatedArticles: ['event-rsvp', 'event-host-tools'],
  }),
  createHelpArticle({
    id: 'event-create', slug: 'create-an-event', title: 'Create an event', category: 'Events',
    description: 'Use Create in Events to fill in event details; campus and account eligibility affect some choices.',
    keywords: ['create event', 'organize', 'host', 'campus', 'online'],
    aliases: ['make an event', 'start a campus event', 'publish event'],
    steps: [
      { title: 'Open event creation', body: 'Open Events and choose “Create”.' },
      { title: 'Fill in the form', body: 'Enter the required event name and description, then choose the event details and audience offered by the form.' },
      { title: 'Choose location details', body: 'For an inside-campus event, use the college selection or verification state requested by Campnect. Online and outside-campus choices use their displayed fields.' },
      { title: 'Create the event', body: 'Review the details and choose the create action.' },
    ],
    notes: ['Permissions and inside-campus eligibility vary by account and verified campus. Follow the form’s validation messages.'],
    relatedArticles: ['event-audience', 'event-host-tools', 'event-discover'],
  }),
  createHelpArticle({
    id: 'event-audience', slug: 'event-audience-and-campus-access', title: 'Event audience and campus access', category: 'Events',
    description: 'Event creation provides Public and Private choices; inside-campus access can require a verified college.',
    keywords: ['event audience', 'public event', 'private event', 'campus eligibility'],
    aliases: ['who can join event', 'private campus event', 'event visibility'],
    body: ['The create form describes Public events as discoverable on campus and Private events as invite-or-approval only. Follow the exact options and eligibility messages shown when creating an event.'],
    notes: ['Event visibility is separate from post audiences and profile privacy.'],
    relatedArticles: ['event-create', 'event-rsvp', 'profile-visibility'],
  }),
  createHelpArticle({
    id: 'event-host-tools', slug: 'event-host-tools-and-announcements', title: 'Event host tools and announcements', category: 'Events',
    description: 'Eligible hosts can see event editing and management controls on Event Detail.',
    keywords: ['event host', 'organizer', 'edit event', 'cohost', 'announcement', 'invite'],
    aliases: ['manage my event', 'add event cohost', 'send event announcement', 'invite attendees'],
    steps: [
      { title: 'Open your event', body: 'Open Event Detail for an event you organize or host.' },
      { title: 'Use visible host tools', body: 'Eligible hosts can use “Edit event details”, send an announcement, or invite by @username when those controls are shown.' },
      { title: 'Check organizer-only controls', body: 'Some co-host management and attendee actions are limited to the event organizer.' },
    ],
    notes: ['End event and Cancel event are distinct confirmation actions with destructive effects described by their dialogs. Do not assume a separate delete control or that ending preserves the event.'],
    relatedArticles: ['event-create', 'event-attendees', 'event-report'],
  }),
  createHelpArticle({
    id: 'event-chat', slug: 'event-chat-availability', title: 'Why is event chat unavailable?', category: 'Events',
    description: 'Event chat access depends on the event state and the account’s event membership or host access.',
    keywords: ['event chat', 'conversation', 'join event', 'closed'],
    aliases: ['cannot open event chat', 'event group chat unavailable', 'chat is closed'],
    steps: [
      { title: 'Check the event status', body: 'Open Event Detail and check whether the event is ended or cancelled.' },
      { title: 'Check your event access', body: 'Join the event or follow the access requirements shown by Campnect.' },
      { title: 'Try Event chat again', body: 'Use the Event chat control when it is available on Event Detail.' },
    ],
    notes: ['Event chat is different from community chat, which is not currently available.'],
    relatedArticles: ['event-rsvp', 'message-send', 'community-chat-unavailable'],
  }),
  createHelpArticle({
    id: 'event-report', slug: 'report-an-event', title: 'Report an event', category: 'Events',
    description: 'Event Detail provides a Report event action for reporting that specific event.',
    keywords: ['report event', 'event safety', 'inappropriate event'],
    aliases: ['flag an event', 'event is unsafe', 'report campus event'],
    steps: [
      { title: 'Open Event Detail', body: 'Open the event you want reviewed.' },
      { title: 'Choose Report event', body: 'Use the report control on Event Detail.' },
      { title: 'Submit the report', body: 'Provide the requested details and submit the report dialog.' },
    ],
    notes: ['Reporting does not guarantee a response time or a particular moderation outcome.'],
    relatedArticles: ['reporting-outcomes', 'event-discover', 'safety-threats'],
  }),
]